import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Upload, FileText, CheckCircle, AlertCircle, Trash2 } from "lucide-react";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";

interface LapeerCountyTextParserProps {
  onImportComplete?: () => void;
}

interface ParsedDocument {
  filingDate: string;
  bookPage: string;
  docReference: string;
  documentType: string;
  grantors: string[];
  grantees: string[];
  legalDescription: string[];
  parcelId: string;
  hasReferral: boolean;
}

interface ImportResult {
  totalDocuments: number;
  referralFiltered: number;
  familyFiltered: number;
  businessFiltered: number;
  bankFiltered: number;
  excludedFiltered: number;
  existingSkipped: number;
  imported: number;
}

const BANK_KEYWORDS = [
  'BANK', 'MORTGAGE', 'CREDIT UNION', 'FEDERAL SAVINGS', 'LOAN',
  'FINANCIAL', 'LENDING', 'CAPITAL', 'FUNDING', 'FANNIE', 'FREDDIE',
  'HUD', 'VETERANS', 'FHA', 'RURAL DEVELOPMENT', 'NATIONSTAR',
  'QUICKEN', 'ROCKET', 'WELLS FARGO', 'CHASE', 'CITIMORTGAGE'
];

const BUSINESS_KEYWORDS = [
  'LLC', 'INC', 'CORP', 'COMPANY', 'LTD', 'HOLDINGS', 'PROPERTIES',
  'ENTERPRISES', 'ASSOCIATES', 'GROUP', 'PARTNERS', 'DEVELOPMENT',
  'INVESTMENTS', 'REALTY', 'REAL ESTATE', 'CONSTRUCTION', 'BUILDERS',
  'SERVICES', 'MANAGEMENT', 'SOLUTIONS'
];

const EXCLUDED_KEYWORDS = ['CHURCH', 'PARISH', 'MINISTRY', 'MINISTRIES', 'CONGREGATION', 'IRA'];

const TRUST_KEYWORDS = ['TRUST', 'TRUSTEE', 'REVOCABLE', 'IRREVOCABLE', 'LIVING TRUST'];

const extractLastName = (name: string): string => {
  const cleaned = name.trim().toUpperCase();
  if (cleaned.includes(',')) {
    return cleaned.split(',')[0].trim().split(/\s+/)[0];
  }
  const parts = cleaned.split(/\s+/);
  return parts[parts.length - 1];
};

const isExcludedEntity = (name: string): boolean => {
  const upper = name.toUpperCase();
  return EXCLUDED_KEYWORDS.some(kw => upper.includes(kw));
};

const isBusinessEntity = (name: string): boolean => {
  const upper = name.toUpperCase();
  return BUSINESS_KEYWORDS.some(kw => upper.includes(kw)) ||
         TRUST_KEYWORDS.some(kw => upper.includes(kw));
};

const isBankEntity = (name: string): boolean => {
  const upper = name.toUpperCase();
  return BANK_KEYWORDS.some(kw => upper.includes(kw));
};

const isFamilyTransfer = (grantors: string[], grantees: string[]): boolean => {
  if (grantors.some(g => isBusinessEntity(g) || isBankEntity(g))) return false;
  if (grantees.some(g => isBusinessEntity(g) || isBankEntity(g))) return false;

  for (const grantor of grantors) {
    const grantorLast = extractLastName(grantor);
    for (const grantee of grantees) {
      const granteeLast = extractLastName(grantee);
      if (grantorLast === granteeLast && grantorLast.length > 1) {
        return true;
      }
    }
  }
  return false;
};

const isBusinessToBusinessTransfer = (grantors: string[], grantees: string[]): boolean => {
  const allGrantorsBiz = grantors.every(g => isBusinessEntity(g) || isBankEntity(g));
  const allGranteesBiz = grantees.every(g => isBusinessEntity(g) || isBankEntity(g));
  return allGrantorsBiz && allGranteesBiz;
};

export function LapeerCountyTextParser({ onImportComplete }: LapeerCountyTextParserProps) {
  const [rawText, setRawText] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const { toast } = useToast();

  const parseDate = (dateStr: string): string => {
    const parts = dateStr.trim().split('/');
    if (parts.length === 3) {
      const [month, day, year] = parts;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
    return dateStr;
  };

  const parseLapeerText = (text: string): ParsedDocument[] => {
    const lines = text.split('\n');
    const documents = new Map<string, ParsedDocument>();

    // Process blocks: each block starts with a date line and ends at the next >>>>> or date line
    let i = 0;
    while (i < lines.length) {
      const line = lines[i].trimEnd();

      // Look for date line: starts with spaces, then MM/DD/YYYY
      const headerMatch = line.match(/^\s*(\d{2}\/\d{2}\/\d{4})\s+(\d{5}-\d{5})\s+(\S+)\s+(.*?)\s*$/);
      if (!headerMatch) {
        i++;
        continue;
      }

      const filingDate = headerMatch[1];
      const bookPage = headerMatch[2];
      const docReference = headerMatch[3];
      const documentType = headerMatch[4].trim();

      // Read subsequent lines until we hit >>>>>> or another date line
      const blockLines: string[] = [];
      i++;
      let referralLine = '';
      while (i < lines.length) {
        const nextLine = lines[i];
        // Check if this is a >>>>>> line
        if (nextLine.includes('>>>>>>')) {
          referralLine = nextLine;
          i++;
          break;
        }
        // Check if this is the start of a new date block
        if (nextLine.match(/^\s*\d{2}\/\d{2}\/\d{4}\s+\d{5}-\d{5}/)) {
          break;
        }
        // Check if line is just a parcel number line (numbers with spaces at end)
        if (nextLine.match(/^\s{50,}\d{2,3}\s+\d{3}\s+\d{3}\s+\d{2}\s*$/)) {
          i++;
          break;
        }
        blockLines.push(nextLine);
        i++;
      }

      // Parse block lines for GRANTOR, GRANTEE, ADDRESS, legal description
      let hasReferral = referralLine.toUpperCase().includes('REFERRAL');

      // Get or create document
      if (!documents.has(docReference)) {
        documents.set(docReference, {
          filingDate,
          bookPage,
          docReference,
          documentType,
          grantors: [],
          grantees: [],
          legalDescription: [],
          parcelId: '',
          hasReferral: false,
        });
      }
      const doc = documents.get(docReference)!;
      if (hasReferral) doc.hasReferral = true;

      // Extract parcel ID from the >>>>>> line
      const parcelMatch = referralLine.match(/(\d{2,3}\s+\d{2,3}\s+\d{3}\s+\d{3}\s+\d{2})\s*$/);
      if (parcelMatch && !doc.parcelId) {
        doc.parcelId = parcelMatch[1].trim();
      }

      for (const bl of blockLines) {
        const trimmed = bl.trim();
        // Check for GRANTOR line (with or without X marker)
        const grantorMatch = trimmed.match(/^(?:X\s+)?GRANTOR\s+(.*)/i);
        if (grantorMatch) {
          const name = grantorMatch[1].trim();
          if (name && !doc.grantors.includes(name)) {
            doc.grantors.push(name);
          }
          continue;
        }
        // Check for GRANTEE line
        const granteeMatch = trimmed.match(/^(?:X\s+)?GRANTEE\s+(.*)/i);
        if (granteeMatch) {
          const name = granteeMatch[1].trim();
          if (name && !doc.grantees.includes(name)) {
            doc.grantees.push(name);
          }
          continue;
        }
        // Check for ADDRESS line
        const addressMatch = trimmed.match(/^(?:X\s+)?ADDRESS\s+(.*)/i);
        if (addressMatch) {
          const addr = addressMatch[1].trim();
          if (addr && !doc.legalDescription.includes(addr)) {
            doc.legalDescription.push(addr);
          }
          continue;
        }
        // Legal description lines (SEC, LOT, subdivision names)
        if (trimmed.match(/^(SEC\s+\d|LOT\s+\d)/i)) {
          if (!doc.legalDescription.includes(trimmed)) {
            doc.legalDescription.push(trimmed);
          }
          continue;
        }
        // Subdivision/plat lines
        if (trimmed.match(/\b(PLAT|SUB|ADDITION|DIVISION)\b/i) && !trimmed.match(/^(GRANTOR|GRANTEE|ADDRESS)/i)) {
          if (!doc.legalDescription.includes(trimmed)) {
            doc.legalDescription.push(trimmed);
          }
        }
      }
    }

    return Array.from(documents.values());
  };

  const processRecords = async () => {
    if (!rawText.trim()) {
      toast({ title: "No data", description: "Please paste Lapeer County data to import", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setImportResult(null);

    try {
      const allDocs = parseLapeerText(rawText);
      setProgress(20);

      const result: ImportResult = {
        totalDocuments: allDocs.length,
        referralFiltered: 0,
        familyFiltered: 0,
        businessFiltered: 0,
        bankFiltered: 0,
        excludedFiltered: 0,
        existingSkipped: 0,
        imported: 0
      };

      // Apply filters
      const validDocs: ParsedDocument[] = [];
      for (const doc of allDocs) {
        // Filter referrals
        if (doc.hasReferral) {
          result.referralFiltered++;
          continue;
        }
        // Need at least a grantor
        if (doc.grantors.length === 0) continue;
        // Filter churches and IRA entities
        if (doc.grantors.some(g => isExcludedEntity(g)) || doc.grantees.some(g => isExcludedEntity(g))) {
          result.excludedFiltered++;
          continue;
        }
        // Filter bank transactions
        if (doc.grantors.some(g => isBankEntity(g)) || doc.grantees.some(g => isBankEntity(g))) {
          result.bankFiltered++;
          continue;
        }
        // Filter business-to-business
        if (doc.grantees.length > 0 && isBusinessToBusinessTransfer(doc.grantors, doc.grantees)) {
          result.businessFiltered++;
          continue;
        }
        // Filter family transfers
        if (doc.grantees.length > 0 && isFamilyTransfer(doc.grantors, doc.grantees)) {
          result.familyFiltered++;
          continue;
        }
        validDocs.push(doc);
      }
      setProgress(50);

      // Check existing in database
      const docRefs = validDocs.map(d => d.docReference);
      if (docRefs.length > 0) {
        const { data: existing } = await supabase
          .from('county_research')
          .select('document_number')
          .eq('county', 'lapeer')
          .in('document_number', docRefs);

        const existingSet = new Set(existing?.map(e => e.document_number) || []);
        setProgress(60);

        const recordsToInsert = validDocs.filter(d => {
          if (existingSet.has(d.docReference)) {
            result.existingSkipped++;
            return false;
          }
          return true;
        });

        if (recordsToInsert.length > 0) {
          const insertData = recordsToInsert.map(doc => ({
            county: 'lapeer',
            document_number: doc.docReference,
            document_type: doc.documentType,
            filing_date: parseDate(doc.filingDate),
            grantor_name: doc.grantors.join('; '),
            grantee_name: doc.grantees.join('; '),
            legal_description: doc.legalDescription.join(' | ') || null,
            notes: [
              doc.parcelId ? `Parcel: ${doc.parcelId}` : null,
              `Book-Page: ${doc.bookPage}`,
            ].filter(Boolean).join(' | '),
            is_potential_owner_finance: true,
            confidence_score: 85,
            detection_reasons: JSON.stringify(['Memorandum Land Contract', 'Lapeer County ROD']),
            search_date: new Date().toISOString(),
            status: 'new',
          }));

          const batchSize = 50;
          for (let i = 0; i < insertData.length; i += batchSize) {
            const batch = insertData.slice(i, i + batchSize);
            const { error } = await supabase.from('county_research').insert(batch);
            if (error) {
              console.error('Insert error:', error);
              throw error;
            }
            result.imported += batch.length;
            setProgress(60 + ((i + batch.length) / insertData.length) * 35);
          }
          backupRecordsToExcel(insertData, 'lapeer', 'lapeer_parser');
        }
      }

      setProgress(100);
      setImportResult(result);
      setRawText("");

      toast({
        title: "Import complete",
        description: `Imported ${result.imported} records from Lapeer County`
      });

      if (onImportComplete) onImportComplete();
    } catch (error) {
      console.error('Import error:', error);
      toast({
        title: "Import failed",
        description: error instanceof Error ? error.message : "An error occurred",
        variant: "destructive"
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const text = await file.text();
    setRawText(text);
    toast({ title: "File loaded", description: `Loaded ${file.name} - ${text.split('\n').length} lines` });
  };

  return (
    <div className="space-y-4">
      {importResult && (
        <Alert className={importResult.imported > 0 ? "border-green-500" : "border-yellow-500"}>
          <CheckCircle className="h-4 w-4" />
          <AlertTitle>Import Results</AlertTitle>
          <AlertDescription>
            <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
              <div>Total Documents Found: <strong>{importResult.totalDocuments}</strong></div>
              <div>Referral Records Filtered: <strong>{importResult.referralFiltered}</strong></div>
              <div>Family Transfers Filtered: <strong>{importResult.familyFiltered}</strong></div>
              <div>Business-to-Business Filtered: <strong>{importResult.businessFiltered}</strong></div>
              <div>Bank Transactions Filtered: <strong>{importResult.bankFiltered}</strong></div>
              <div>Church/IRA Filtered: <strong>{importResult.excludedFiltered}</strong></div>
              <div>Already in Database: <strong>{importResult.existingSkipped}</strong></div>
              <div className="text-green-600">Successfully Imported: <strong>{importResult.imported}</strong></div>
            </div>
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Lapeer County, MI - Land Contract Parser
          </CardTitle>
          <CardDescription>
            Paste search results from Lapeer County Register of Deeds.
            Automatically deduplicates by document reference, filters out records with referrals,
            family transfers, business-to-business, and bank transactions.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button variant="outline" asChild>
              <label className="cursor-pointer">
                <Upload className="h-4 w-4 mr-2" />
                Upload Text File
                <input type="file" accept=".txt" onChange={handleFileUpload} className="hidden" />
              </label>
            </Button>
            <Button variant="outline" onClick={() => setRawText("")} disabled={!rawText}>
              <Trash2 className="h-4 w-4 mr-2" />
              Clear
            </Button>
          </div>

          <Textarea
            placeholder={`Paste Lapeer County search results here...

Expected format (multi-line blocks per document):
   12/29/2022    03332-00511   1C-3009           MEMORANDUM LAND CONTRACT
   GRANTOR          RIEBLING, KURT F.
 X GRANTEE          THAO, DEBIE
   LOT 78  (PART OF)                                    CITY OF LAPEER
>>>>>> ADDITIONAL PARTIES AND DESCRIPTIONS

Records with "REFERRAL" in the >>>>>> line will be excluded.`}
            value={rawText}
            onChange={(e) => setRawText(e.target.value)}
            className="min-h-[300px] font-mono text-xs"
          />

          {isProcessing && (
            <div className="space-y-2">
              <Progress value={progress} />
              <p className="text-sm text-muted-foreground text-center">
                Processing... {Math.round(progress)}%
              </p>
            </div>
          )}

          <Button onClick={processRecords} disabled={isProcessing || !rawText.trim()} className="w-full">
            {isProcessing ? "Processing..." : "Parse & Import Records"}
          </Button>
        </CardContent>
      </Card>

      <Alert>
        <AlertCircle className="h-4 w-4" />
        <AlertTitle>Filtering Rules</AlertTitle>
        <AlertDescription className="text-xs space-y-1">
          <p>• <strong>Referrals</strong>: Records with "REFERRAL" in the additional info line → filtered out</p>
          <p>• <strong>Family transfers</strong>: Same last name on grantor/grantee sides → filtered out</p>
          <p>• <strong>Business-to-business</strong>: Both parties are entities (LLC, Inc, Trust, etc.) → filtered out</p>
          <p>• <strong>Bank transactions</strong>: Any party is a bank/mortgage company → filtered out</p>
          <p>• <strong>Church/IRA</strong>: Any party is a church, ministry, or IRA → filtered out</p>
          <p>• <strong>Duplicates</strong>: Multiple blocks per document are consolidated into one record</p>
        </AlertDescription>
      </Alert>
    </div>
  );
}
